import * as Yup from 'yup';
import {
  InputFieldProps,
  sortGeographicAreasChildrenAlphabetically
} from 'components/Forms';
import { getRequiredErrorMessageNew } from 'constants/validation';
import { DateTime } from 'luxon';
import { GeographicAreaParent } from 'models/GeographicArea';
import CapacityTypeModel from 'models/CapacityType';

export enum ReserveCapacityFormFields {
  Area = 'area',
  CapacityType = 'capacityType',
  ValidFrom = 'validFrom',
  ValidUntil = 'validUntil',
  Percentage = 'percentage',
  ReleaseAt = 'releaseAt',
  EffectiveDays = 'effectiveDays'
}

const percentageOptions = Array.from({ length: 100 }, (_, i) => ({
  label: `${i + 1}%`,
  value: `${i + 1}`
}));

const releaseAtOptions = Array.from({ length: 50 }, (_, i) => ({
  label: `${i + 1} (day)`,
  value: `${i + 1}`
}));

export const getReserveCapacityFormFields = (
  geographicAreas: GeographicAreaParent[],
  capacityTypes: CapacityTypeModel[]
): InputFieldProps[] => [
  {
    id: ReserveCapacityFormFields.Area,
    required: true,
    name: ReserveCapacityFormFields.Area,
    qaId: ReserveCapacityFormFields.Area,
    inputType: 'singleselect',
    label: 'Area',
    options: sortGeographicAreasChildrenAlphabetically(geographicAreas)
  },
  {
    id: ReserveCapacityFormFields.CapacityType,
    required: true,
    name: ReserveCapacityFormFields.CapacityType,
    qaId: ReserveCapacityFormFields.CapacityType,
    inputType: 'singleselect',
    label: 'Capacity Type',
    options: capacityTypes.map(({ id, name }) => ({
      label: name,
      value: id
    }))
  },
  {
    id: ReserveCapacityFormFields.ValidFrom,
    required: true,
    name: ReserveCapacityFormFields.ValidFrom,
    qaId: ReserveCapacityFormFields.ValidFrom,
    inputType: 'dateNew',
    label: 'Valid From',
    placeholder: 'DD/MM/YYYY'
  },
  {
    id: ReserveCapacityFormFields.ValidUntil,
    required: true,
    name: ReserveCapacityFormFields.ValidUntil,
    qaId: ReserveCapacityFormFields.ValidUntil,
    inputType: 'dateNew',
    label: 'Valid Until',
    placeholder: 'DD/MM/YYYY'
  },
  {
    id: ReserveCapacityFormFields.Percentage,
    required: true,
    name: ReserveCapacityFormFields.Percentage,
    qaId: ReserveCapacityFormFields.Percentage,
    inputType: 'singleselect',
    label: '% Capacity',
    options: percentageOptions
  },
  {
    id: ReserveCapacityFormFields.ReleaseAt,
    required: true,
    name: ReserveCapacityFormFields.ReleaseAt,
    qaId: ReserveCapacityFormFields.ReleaseAt,
    inputType: 'singleselect',
    label: 'Release At',
    options: releaseAtOptions
  },
  {
    id: ReserveCapacityFormFields.EffectiveDays,
    required: true,
    name: ReserveCapacityFormFields.EffectiveDays,
    qaId: ReserveCapacityFormFields.EffectiveDays,
    inputType: 'weekdays',
    label: 'Effective Days'
  }
];

export function sanitizeDateForComparison(date: string) {
  const dateISO = DateTime.fromISO(date);
  if (dateISO.isValid) {
    return dateISO;
  }
  return DateTime.fromFormat(date, 'dd/MM/yyyy');
}

export const validationSchema = Yup.object().shape(
  {
    [ReserveCapacityFormFields.Area]: Yup.string().required(
      getRequiredErrorMessageNew('Area', true)
    ),
    [ReserveCapacityFormFields.CapacityType]: Yup.string().required(
      getRequiredErrorMessageNew('Capacity Type', true)
    ),
    [ReserveCapacityFormFields.ValidFrom]: Yup.string()
      .required(getRequiredErrorMessageNew('Date', true))
      .nullable()
      .when(
        ReserveCapacityFormFields.ValidUntil,
        (
          validUntil: string,
          schema: Yup.StringSchema<string | null | undefined>
        ) =>
          schema.test({
            test: (validFrom: string | null | undefined) => {
              if (validFrom && validUntil) {
                return (
                  sanitizeDateForComparison(validFrom).diff(
                    sanitizeDateForComparison(validUntil)
                  ).milliseconds <= 0
                );
              }
              return true;
            },
            message: 'Valid from date must be before valid to date'
          })
      ),
    [ReserveCapacityFormFields.ValidUntil]: Yup.string()
      .required(getRequiredErrorMessageNew('Date', true))
      .nullable()
      .when(
        ReserveCapacityFormFields.ValidFrom,
        (
          validFrom: string,
          schema: Yup.StringSchema<string | null | undefined>
        ) =>
          schema.test({
            test: (validUntil: string | null | undefined) => {
              if (validFrom && validUntil) {
                return (
                  sanitizeDateForComparison(validFrom).diff(
                    sanitizeDateForComparison(validUntil)
                  ).milliseconds <= 0
                );
              }
              return true;
            },
            message: 'Valid to date must be after valid from date'
          })
      )
      .test('is-not-past', 'Valid until cannot be in the past', value => {
        if (!value) {
          return true;
        }
        return (
          sanitizeDateForComparison(value).startOf('day') >=
          DateTime.local().startOf('day')
        );
      }),
    [ReserveCapacityFormFields.Percentage]: Yup.string().required(
      getRequiredErrorMessageNew('Capacity percentage', false)
    ),
    [ReserveCapacityFormFields.ReleaseAt]: Yup.string().required(
      getRequiredErrorMessageNew('Number of days', false)
    ),
    [ReserveCapacityFormFields.EffectiveDays]: Yup.object()
      .nullable()
      .test('at-least-one-day', 'At least one day must be selected', value => {
        if (!value) {
          return false;
        }
        return Object.values(value).some(Boolean);
      })
  },
  [[ReserveCapacityFormFields.ValidFrom, ReserveCapacityFormFields.ValidUntil]]
);
